import { JOURNAL } from '@/hooks/api/journals/types';
import { Gender } from '@/hooks/api/patients/types';
import { Audio, Picture } from '@/hooks/api/types';

import {
  ActivityEvaluation,
  ActivityScoreConfig,
  QuestionSet,
} from '../assigned-activities/types';

export enum ActivityStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  RETIRED = 'RETIRED',
  UNKNOWN = 'UNKNOWN',
}

export const PATHWAY = 'PATHWAY';

export enum ActivityType {
  DIARY = 'DIARY',
  QUESTIONNAIRE = 'QUESTIONNAIRE',
  EXERCISE = 'EXERCISE',
  TRAINING = 'TRAINING',
  CUSTOM_EXERCISE = 'CUSTOM_EXERCISE',
}

export type AllActivityType = ActivityType | typeof JOURNAL;
export type PathwayAndActivityType = AllActivityType | typeof PATHWAY;

export enum ActivityLanguage {
  DE = 'DE',
  FR = 'FR',
}

export enum ActivityItemType {
  SECTION_TITLE = 'SECTION_TITLE',
  PAGE_BREAK = 'PAGE_BREAK',
  PRACTITIONER_INPUT_TEXT = 'PRACTITIONER_INPUT_TEXT',
  PRACTITIONER_INPUT_AUDIO = 'PRACTITIONER_INPUT_AUDIO',
  PRACTITIONER_INPUT_VIDEO_LINK = 'PRACTITIONER_INPUT_VIDEO_LINK',
  PRACTITIONER_INPUT_IMAGE = 'PRACTITIONER_INPUT_IMAGE',
  PATIENT_RESPONSE_SHORT_ANSWER = 'PATIENT_RESPONSE_SHORT_ANSWER',
  PATIENT_RESPONSE_LONG_ANSWER = 'PATIENT_RESPONSE_LONG_ANSWER',
  PATIENT_RESPONSE_NUMBER = 'PATIENT_RESPONSE_NUMBER',
  PATIENT_RESPONSE_SINGLE_CHOICE = 'PATIENT_RESPONSE_SINGLE_CHOICE',
  PATIENT_RESPONSE_MULTIPLE_CHOICE = 'PATIENT_RESPONSE_MULTIPLE_CHOICE',
  PATIENT_RESPONSE_SORTING = 'PATIENT_RESPONSE_SORTING',
  PATIENT_RESPONSE_LIKERT_SCALE = 'PATIENT_RESPONSE_LIKERT_SCALE',
  PATIENT_RESPONSE_LINEAR_SCALE = 'PATIENT_RESPONSE_LINEAR_SCALE',
  PATIENT_RESPONSE_PATIENT_RECORD = 'PATIENT_RESPONSE_PATIENT_RECORD',
  PATIENT_RESPONSE_SMILEY_SCALE = 'PATIENT_RESPONSE_SMILEY_SCALE',
  PATIENT_RESPONSE_TIME_INPUT_ANSWER = 'PATIENT_RESPONSE_TIME_INPUT_ANSWER',
  PATIENT_RESPONSE_SITUATIONAL_TIME_INPUT_ANSWER = 'PATIENT_RESPONSE_SITUATIONAL_TIME_INPUT_ANSWER',
}

export enum TreatmentPlanPatientRecordType {
  GOAL = 'GOAL',
  RESOURCE = 'RESOURCE',
  MENTAL_CHALLENGE = 'MENTAL_CHALLENGE',
}

export enum SafetyPlanPatientRecordType {
  PREVENTION = 'PREVENTION',
  VULNERABILITY = 'VULNERABILITY',
  WARNING_SIGN = 'WARNING_SIGN',
  PERCEPTION = 'PERCEPTION',
  SKILL = 'SKILL',
  EMERGENCY_DETECTION = 'EMERGENCY_DETECTION',
  EMERGENCY_STRATEGY = 'EMERGENCY_STRATEGY',
  MOTIVATION_TO_LIVE = 'MOTIVATION_TO_LIVE',
}

export type PatientRecordType = TreatmentPlanPatientRecordType &
  SafetyPlanPatientRecordType;

export interface AnswerOption {
  name: string;
  value: string;
  order?: number;
}

export enum RecommendedFrequency {
  ONE_TIME = 'ONE_TIME',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  EVERY_FOUR_WEEKS = 'EVERY_FOUR_WEEKS',
}

export enum AudioContentFilter {
  ANY_TYPE = 'ANY_TYPE',
  WITH_AUDIO = 'WITH_AUDIO',
  NO_AUDIO = 'NO_AUDIO',
}

export enum VideoContentFilter {
  ANY_TYPE = 'ANY_TYPE',
  WITH_VIDEO = 'WITH_VIDEO',
  NO_VIDEO = 'NO_VIDEO',
}

export enum PublishedByFilter {
  YLAH = 'YLAH',
  INSTITUTION = 'INSTITUTION',
}

export enum AccessibilityRestriction {
  NO_RESTRICTION = 'NO_RESTRICTION',
  VISUALLY_IMPAIRED = 'VISUALLY_IMPAIRED',
  HEARING_IMPAIRED = 'HEARING_IMPAIRED',
}

export enum ActivitySetting {
  SELF_HELP = 'SELF_HELP',
  ONE_TIME_PRESCRIPTION = 'ONE_TIME_PRESCRIPTION',
  PERMANENT_PERSCRIPTION = 'PERMANENT_PRESCRIPTION',
  ASSISTED = 'ASSISTED',
}

export enum QualityBaseType {
  EVIDENCE_BASED = 'EVIDENCE_BASED',
  EMINENCE_BASED = 'EMINENCE_BASED',
}

export enum AgeGroup {
  ADOLESCENTS = 'ADOLESCENTS',
  ADULTS = 'ADULTS',
  ELDERLY = 'ELDERLY',
}

export enum TransdiagnosticSubGroup {
  START = 'START',
  PROGRESS = 'PROGRESS',
  COMPLETION = 'COMPLETION',
}

export enum Indication {
  TRANSDIAGNOSTIC = 'TRANSDIAGNOSTIC',
  DEPRESSION = 'DEPRESSION',
  ANXIETY_DISORDER = 'ANXIETY_DISORDER',
  ADDICTION = 'ADDICTION',
  OTHER = 'OTHER',
}

export enum DateRange {
  LAST_TWO_WEEKS = 'LAST_TWO_WEEKS',
  LAST_MONTH = 'LAST_MONTH',
  LAST_THREE_MONTHS = 'LAST_THREE_MONTHS',
  CUSTOM_DATE_RANGE = 'CUSTOM_DATE_RANGE',
}

export interface ActivityModuleData {
  code: string;
  name: string;
  indication: string;
  subgroup?: string;
}

export enum CustomActivityVisibility {
  PRACTITIONER = 'PRACTITIONER',
  INSTITUTION = 'INSTITUTION',
}

export interface QualityBase {
  type: QualityBaseType;
  description: Array<TextWithLink>;
}

export interface TextWithLink {
  label: string;
  link?: string;
}

export type EnableOperator = 'exists' | '=' | '!=' | '>' | '<' | '>=' | '<=';

export type EnableBehavior = 'all' | 'any';

export interface EnableWhen {
  question: string;
  operator: EnableOperator;
  answer: string;
}

export interface ConditionalLogic {
  enableWhen: Array<EnableWhen>;
  enableBehavior: EnableBehavior;
  isPageCondition: boolean;
}

export interface ActivityItem {
  id?: string;
  activityItemType: ActivityItemType;
  recordType?: PatientRecordType;
  order: number;
  displayValue: string;
  picture?: Picture;
  audio?: Audio;
  initialValue?: string;
  answerOptions?: Array<AnswerOption>;
  required?: boolean;
  conditionalLogic?: ConditionalLogic;
  questionNumber?: number;
  isConditionallyHidden?: boolean;
}

export interface Activity {
  id: string;
  status: ActivityStatus;
  activityType: ActivityType;
  language: ActivityLanguage;
  title: string;
  description?: string;
  picture?: Picture;
  createdAt: string; //date-time
  audioContent?: boolean;
  videoContent?: boolean;
  publisher?: string;
  recommendedFrequency?: RecommendedFrequency;
  isConditional: boolean;
}

export interface ActivityPage {
  items: Array<ActivityItem>;
}

export interface DetailedActivity extends Activity {
  indication?: Array<Indication>;
  contraIndication?: string;
  instruction?: string;
  method?: Array<string>;
  suggestedDuration?: number;
  pages: Array<ActivityPage>;
  gender?: Array<Gender>;
  qualityBase?: QualityBase;
  accessibilityRestriction?: AccessibilityRestriction;
  setting?: Array<ActivitySetting>;
  references?: Array<TextWithLink>;
  author?: string;
  patientAgeGroup?: Array<AgeGroup>;
  activityGoal?: string;
  modules?: Array<string>;
  practitionerFeedback?: string;
  customActivityVisibility?: CustomActivityVisibility;
  areIntellectualRightsConfirmed?: boolean;
  updatedAt?: string;
  gatherFeedback: boolean;
  evaluations: Array<ActivityEvaluation>;
  questionSets: Array<QuestionSet>;
  scores?: Array<ActivityScoreConfig>;
  institutionId?: string;
  assignedPathwayId?: string;
}

// Needed for custom activity duplicate
export interface DetailedActivityWithOptionalId
  extends Omit<DetailedActivity, 'id'> {
  id?: string;
}

export enum SmileyScale {
  DoNotKnow = '0',
  VeryBad = '1',
  Poor = '2',
  Mediocre = '3',
  Good = '4',
  VeryGood = '5',
}

export interface HomeworkActivity {
  title: string;
  description?: string;
  language: ActivityLanguage;
  gatherFeedback: boolean;
  pages: Array<ActivityPage>;
  picture?: Picture;
}
